import { ConvexHull } from 'three/addons/math/ConvexHull.js';
import { Vector3 } from 'three';
import type { WorldData, Vec3, Volume, Traversal, EntityMeta } from './types';
import { distance } from './types';
export type EntityKind = 'region' | 'poi' | 'volume' | 'checkpoint' | 'activity' | 'object';
export type EntityRef = {
    kind: EntityKind;
    id: string;
};
export const collection = { region: 'regions', poi: 'pois', volume: 'volumes', checkpoint: 'checkpoints', activity: 'activities', object: 'objects' } as const;
export function entity(world: WorldData, ref: EntityRef) { return (world[collection[ref.kind]] || []).find(e => String(e.id) === ref.id); }
export function positionOf(e: any): Vec3 { return [...(e.center || e.position || e.a || e.start || e.gates?.[0] || [0, 15, 0])] as Vec3; }
function stableUUID(text: string) { let h = 2166136261; for (const c of text)
    h = Math.imul(h ^ c.charCodeAt(0), 16777619); let hex = ''; for (let i = 0; i < 4; i++) {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    hex += (h >>> 0).toString(16).padStart(8, '0');
} return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-5${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20)}`; }
export function editorDefaults(world: WorldData) { world.checkpoints ??= []; world.tagRegistry ??= []; for (const [kind, key] of Object.entries(collection))
    for (const e of world[key as typeof collection[EntityKind]] || []) {
        e.uuid ??= stableUUID(`${world.id}:${kind}:${e.id}`);
        e.tags ??= [];
    } }
export function regionPath(world: WorldData, id: string) { const path: string[] = [], seen = new Set<string>(); let r = world.regions.find(r => r.id === id); while (r && !seen.has(r.id)) {
    seen.add(r.id);
    path.unshift(r.name);
    r = world.regions.find(p => p.id === r!.parent);
} return path.join(' / '); }
export function regionAt(world: WorldData, p: Vec3) {
    const matches = world.regions.filter(r => r.volume ? containsVolume(world, r.volume, p) : distance(r.center, p) <= r.radius);
    return matches.sort((a, b) => (b.priority || 0) - (a.priority || 0) || regionPath(world, b.id).split(' / ').length - regionPath(world, a.id).split(' / ').length || a.radius - b.radius || a.id.localeCompare(b.id))[0] || world.regions.reduce((a, b) => distance(a.center, p) < distance(b.center, p) ? a : b);
}
export function segmentDistance(p: Vec3, a: Vec3, b: Vec3) { const d = b.map((n, i) => n - a[i]), den = d.reduce((n, v) => n + v * v, 0), t = Math.max(0, Math.min(1, d.reduce((n, v, i) => n + v * (p[i] - a[i]), 0) / Math.max(den, 1e-9))); return distance(p, a.map((n, i) => n + t * d[i])); }
const hullCache = new WeakMap<Volume, {
    key: string;
    hull: ConvexHull;
}>();
export function containsVolume(world: WorldData, id: string, p: Vec3, seen = new Set<string>()): boolean {
    const v = world.volumes.find(v => v.id === id);
    if (!v || seen.has(id))
        return false;
    seen = new Set(seen).add(id);
    if (v.shape === 'composite')
        return (v.members || []).some(member => containsVolume(world, member, p, seen));
    const q = p.map((n, i) => n - v.position[i]) as Vec3, [x, y, z] = q, [sx, sy, sz] = v.size;
    if (v.shape === 'box')
        return Math.abs(x) <= sx / 2 && Math.abs(y) <= sy / 2 && Math.abs(z) <= sz / 2;
    if (v.shape === 'sphere')
        return x * x / (sx * sx) + y * y / (sy * sy) + z * z / (sz * sz) <= 1;
    if (v.shape === 'cylinder')
        return x * x / (sx * sx) + z * z / (sz * sz) <= 1 && Math.abs(y) <= sy / 2;
    if (v.shape === 'capsule') {
        const half = Math.max(0, sy / 2 - sx);
        return segmentDistance(q, [0, -half, 0], [0, half, 0]) <= sx;
    }
    if (v.shape === 'spline')
        return Math.abs(y) <= sy / 2 && (v.points || []).slice(1).some((b, i) => segmentDistance([x, 0, z], [v.points![i][0], 0, v.points![i][2]], [b[0], 0, b[2]]) <= sx);
    if (v.shape === 'prism') {
        if (Math.abs(y) > sy / 2)
            return false;
        const pts = v.points || [];
        if(pts.some((a,i)=>segmentDistance([x,0,z],[a[0],0,a[2]],[pts[(i+1)%pts.length][0],0,pts[(i+1)%pts.length][2]])<1e-7))return true;
        let inside = false;
        for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
            const a = pts[i], b = pts[j];
            if (((a[2] > z) !== (b[2] > z)) && x < (b[0] - a[0]) * (z - a[2]) / (b[2] - a[2]) + a[0])
                inside = !inside;
        }
        return inside;
    }
    if (v.shape === 'convex' && (v.points?.length || 0) >= 4) {
        const key = JSON.stringify(v.points);
        let cache = hullCache.get(v);
        if (cache?.key !== key) {
            try {
                cache = { key, hull: new ConvexHull().setFromPoints(v.points!.map(p => new Vector3(...p))) };
                hullCache.set(v, cache);
            }
            catch {
                return false;
            }
        }
        return cache!.hull.containsPoint(new Vector3(...q));
    }
    return false;
}
export function uniqueId(world: WorldData, kind: EntityKind) { const used = new Set((world[collection[kind]] || []).map(e => String(e.id))); let i = 1; while (used.has(`${kind}-${i}`))
    i++; return `${kind}-${i}`; }
export function makeEntity(world: WorldData, kind: Exclude<EntityKind, 'object'>, p: Vec3) {
    const meta: EntityMeta = { uuid: crypto.randomUUID(), tags: [], priority: 0 }, id = uniqueId(world, kind), region = regionAt(world, p).id;
    if (kind === 'region')
        return { ...meta, id, name: 'New region', center: [...p], radius: 12, biome: 'great_canopy', color: '#83a969', parent: region };
    if (kind === 'poi')
        return { ...meta, id, name: 'New discovery', position: [...p], radius: 6, description: 'A new place to explore.', category: 'Vista', region };
    if (kind === 'volume')
        return { ...meta, id, name: 'New area', position: [...p], size: [8, 8, 8], shape: 'box', purpose: 'safe', region };
    if (kind === 'checkpoint')
        return { ...meta, id, name: 'New checkpoint', position: [...p], physicsVersion: world.physicsVersion, region };
    return { ...meta, id, name: 'New canopy race', gates: [[...p], [p[0] + 4, p[1], p[2] + 5]], radius: 2, medals: [20, 35, 60], region, start: [...p] };
}
export function deleteEntity(world: WorldData, ref: EntityRef) {
    const e = entity(world, ref) as any;
    if (!e)
        return;
    if (e.locked)
        throw Error('Unlock this entity before removing it.');
    if (ref.kind === 'region' && (world.regions.some(r => r.parent === ref.id) || [...world.objects, ...world.pois, ...world.volumes, ...world.activities, ...world.checkpoints || []].some((e: any) => e.region === ref.id)))
        throw Error('Move this region’s children and contents before removing it.');
    if (ref.kind === 'region' && world.regions.length <= 1)
        throw Error('Keep at least one region.');
    if (ref.kind === 'volume' && (world.volumes.some(v => v.members?.includes(ref.id)) || world.regions.some(r => r.volume === ref.id)))
        throw Error('This volume is referenced by a region or composite.');
    if (ref.kind === 'poi' && (ref.id === 'home' || world.pois.some(p => p.requires?.includes(ref.id))))
        throw Error('This discovery is required by the game or another POI.');
    if (ref.kind === 'activity' && world.activities.indexOf(e) < 2)
        throw Error('The two built-in activities can be edited but are required by the game.');
    const list = world[collection[ref.kind]] as any[];
    list.splice(list.indexOf(e), 1);
}
export type Proposal = {
    name: string;
    objects: Traversal[];
    replace: string[];
};
/** Existing contacts, including their UUIDs, geometry and lock flags, are never regenerated. */
export function treeProposal(world: WorldData, contact: Traversal, variant: number): Proposal {
    const owned = world.objects.filter(o => o.generatedFor === contact.uuid), retained = owned.filter(o => o.locked), region = contact.region;
    let next = Math.max(0, ...world.objects.map(o => o.id)) + 1;
    const angle = variant * 2.39996, offset: Vec3 = [Math.cos(angle) * 3, 0, Math.sin(angle) * 3], c = contact.a;
    const root: Vec3 = [c[0] + offset[0], Math.max(-2, c[1] - 12), c[2] + offset[2]], top: Vec3 = [root[0], c[1] + 3, root[2]];
    const specs: {
        type: number;
        a: Vec3;
        b: Vec3;
        radius: number;
    }[] = [{ type: 5, a: root, b: top, radius: .55 }, { type: 1, a: [root[0], c[1], root[2]], b: [...c], radius: .22 }];
    for (let i = 0; i < 3; i++) {
        const t = angle + i * Math.PI * 2 / 3, y = c[1] + 1 + i * .7;
        specs.push({ type: 1, a: [root[0], y, root[2]], b: [root[0] + Math.cos(t) * 4, y + .7, root[2] + Math.sin(t) * 4], radius: .18 });
    }
    const objects = specs.map((s, i) => { const old = owned.find(o => o.generationSlot === i); return old?.locked ? structuredClone(old) : { ...s, id: old?.id ?? next++, uuid: old?.uuid ?? crypto.randomUUID(), name: `${contact.name} · ${i === 0 ? 'trunk' : 'limb ' + i}`, region, tags: ['procedural'], generatedFor: contact.uuid, generationSlot: i }; });
    for (const o of retained)
        if (!objects.some(n => n.uuid === o.uuid))
            objects.push(structuredClone(o));
    if (world.objects.length - owned.length + objects.length > 2000)
        throw Error('The generated tree would exceed the map’s 2,000-object budget.');
    return { name: `Tree around ${contact.name}`, objects, replace: owned.map(o => o.uuid) };
}
export function applyProposal(world: WorldData, proposal: Proposal) { world.objects = world.objects.filter(o => !proposal.replace.includes(o.uuid)); world.objects.push(...structuredClone(proposal.objects)); }
export function physicsSignature(world: WorldData) { return JSON.stringify(world.objects.map(o => [o.id, o.type, o.a, o.b, o.radius])); }
